"use client";

import {
  Building,
  Building2,
  LogOut,
  Menu,
  Settings,
  User,
} from "lucide-react";

import { usePathname, useSearchParams } from "next/navigation";
import Link from "next/link";

import { INBOUND_GPT_SUBROUTES } from "~/lib/inbound-gpt/paths";
import { MARKETING_SOLUTION_SLUGS } from "~/lib/inbound-gpt/solution-slugs";

import { TooltipProvider } from "~/components/ui/tooltip";

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "~/components/ui/resizable";

import React, { Suspense } from "react";
import { cn } from "~/lib/utils";
import { type NavProps } from "./nav";
import Image from "next/image";
import { UserProfile } from "./user-profile";
import { AppShellSidebar } from "./app-shell-sidebar";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "~/components/ui/sheet";
import { useMediaQuery } from "~/hooks/use-media-query";
import {
  useAuth,
  useClerk,
  useOrganization,
  useOrganizationList,
} from "@clerk/nextjs";
import ClerkOrgSwitcher from "./clerk-org-switcher";
import { AppShellHeaderSlotProvider } from "./app-shell-header-controls";
import { Button } from "~/components/ui/button";

import { useCrispChat } from "~/hooks/use-crisp-chat";
import { useProfileUpdateListener } from "~/hooks/use-profile-update-listener";
import { OrganizationRole } from "~/server/api/routers/router-utils/pure-utils";
import { api } from "~/trpc/react";
import { EnsureMarketingPortalAccess } from "~/components/ensure-marketing-portal-access";
import { useBrand } from "~/config/brands";
import { getPostSignOutRedirectUrl } from "~/lib/auth-paths";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";

export function AppShell({
  children,
  defaultIsCollapsed = false,
  defaultLayout = [30, 80],
  collapsedSize = 4,
  scannerHost = false,
}: {
  children: React.ReactNode;
  defaultIsCollapsed: boolean;
  defaultLayout: number[];
  collapsedSize?: number;
  scannerHost?: boolean;
}) {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultIsCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [headerSlotEl, setHeaderSlotEl] = React.useState<HTMLElement | null>(
    null,
  );
  // Avoid remount loops: only update when the DOM node identity actually changes.
  const setHeaderSlotRef = React.useCallback((node: HTMLDivElement | null) => {
    setHeaderSlotEl((prev) => (prev === node ? prev : node));
  }, []);
  const { toggleChat } = useCrispChat();
  const brand = useBrand();

  const orgsList = useOrganizationList();
  const org = useOrganization();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { orgRole, isLoaded: authIsLoaded, isSignedIn, orgId: authOrgId } = useAuth();
  /** Org Settings is admin-only — hide from members and external collaborators. */
  const isOrgAdmin = orgRole === OrganizationRole.Admin;
  const isHiddenForNonAdmins = !isOrgAdmin;
  const { openUserProfile, openOrganizationProfile, signOut } = useClerk();
  const chrome = getMarketingChrome();
  const isSiteGlue =
    brand.id === "siteglue" || chrome.brandId === "siteglue";

  // Listen for profile updates
  useProfileUpdateListener();

  // Check if we should skip shell layout first
  const isInSigninSignupRoute =
    pathname.includes("/sign-in") || pathname.includes("/sign-up");
  const isWidgetRoute = pathname.includes("/w/");
  const isWatchRoute = pathname.startsWith("/watch/");
  const isChatRoute = pathname.includes("/chat/");
  const isWidgetPreviewRoute = pathname.includes("/widget-preview");
  const isAgentRoute = pathname.includes("/agent/"); // NEW: Skip shell for agent CV pages
  const isPublicOnboardingRoute = pathname.startsWith("/onboarding/");
  /** Full-screen Inbound GPT Builder (Replit-style: no global nav / sidebar) */
  const isBuilderRoute = /\/projects\/[^/]+\/builder\/?$/.test(pathname);
  const isAdminRoute = pathname.startsWith("/admin");
  const isLandingPage = pathname === "/";
  const isAboutPage = pathname === "/about" || pathname.startsWith("/about/");
  const isFeaturesPage = pathname === "/features";
  const isProductPage = pathname === "/product" || pathname.startsWith("/product/");
  const isInboundGptPage =
    INBOUND_GPT_SUBROUTES.some(
      (slug) => pathname === `/${slug}` || pathname.startsWith(`/${slug}/`),
    ) ||
    MARKETING_SOLUTION_SLUGS.some((slug) => pathname === `/${slug}`);
  const isAgentsHireCustomRoute = pathname.startsWith("/agents/hire/custom");
  // Keep hire/custom immersive for guests AND signed-in users (no dashboard sidebar).
  const isSiteGlueOnboardRoute =
    pathname === "/onboard" || pathname.startsWith("/onboard/");
  const isStartFlowRoute = pathname === "/start" || pathname.startsWith("/start/");
  const isStrategyCallRoute = pathname === "/book" || pathname.startsWith("/book/");
  const isAgentReadyRoute =
    scannerHost || pathname === "/agent-ready" || pathname.startsWith("/agent-ready/");
  /** SiteGlue /onboard embeds inbox & contacts in an iframe — no agent-list sidebar. */
  const isOnboardEmbed = searchParams.get("embed") === "onboard";
  const isAgentsMarketplacePage =
    pathname === "/agents" || pathname.startsWith("/agents/");
  const isPricingPage = pathname === "/pricing";
  const isEmployersPage = pathname.startsWith("/employers");
  const shallSkipShellLayout =
    isInSigninSignupRoute ||
    isWidgetRoute ||
    isWatchRoute ||
    isChatRoute ||
    isWidgetPreviewRoute ||
    isLandingPage ||
    isAboutPage ||
    isFeaturesPage ||
    isProductPage ||
    isInboundGptPage ||
    isAgentsMarketplacePage ||
    isPricingPage ||
    isEmployersPage ||
    isAgentRoute ||
    isPublicOnboardingRoute ||
    isSiteGlueOnboardRoute ||
    isStartFlowRoute ||
    isStrategyCallRoute ||
    isAgentReadyRoute ||
    isOnboardEmbed ||
    isBuilderRoute ||
    isAdminRoute;

  // Fetch all projects for AI Agents list - ONLY if not on widget/public routes AND user is logged in
  const isUserLoggedIn = authIsLoaded && isSignedIn;
  const activeOrgId = org.organization?.id ?? authOrgId ?? undefined;
  const projectsQueryEnabled =
    authIsLoaded &&
    org.isLoaded &&
    isUserLoggedIn &&
    !!activeOrgId &&
    !shallSkipShellLayout;

  const projectsQuery = api.project.getAllProjects.useQuery(
    {
      organisationId: activeOrgId,
      includeArchived: false,
      status: "active",
    },
    {
      enabled: projectsQueryEnabled,
      staleTime: 30_000,
    },
  );

  const projects = projectsQuery.data;

  // Disabled queries report isLoading=false — wait for Clerk + first fetch before "not found"
  const isSidebarAgentsLoading =
    isUserLoggedIn &&
    !shallSkipShellLayout &&
    (!projectsQueryEnabled ||
      projectsQuery.isPending ||
      projectsQuery.isLoading);

  React.useEffect(() => {
    if (isDesktop) setMobileNavOpen(false);
  }, [isDesktop]);

  React.useEffect(() => {
    if (isCollapsed) {
      document
        .querySelectorAll(
          ".cl-organizationPreviewTextContainer, .cl-organizationSwitcherTriggerIcon, .cl-userPreviewTextContainer",
        )
        .forEach((el) => el.classList.add("hidden"));
      const orgSwitcherButton = document.querySelector(
        ".cl-rootBox",
      ) as HTMLElement;
      if (orgSwitcherButton) {
        // Reset styles when not collapsed
        orgSwitcherButton.style.margin = "auto";
      }
    } else {
      document
        .querySelectorAll(
          ".cl-organizationPreviewTextContainer, .cl-organizationSwitcherTriggerIcon, .cl-userPreviewTextContainer",
        )
        .forEach((el) => el.classList.remove("hidden"));
      const orgSwitcherButton = document.querySelector(
        ".cl-rootBox",
      ) as HTMLElement;
      if (orgSwitcherButton) {
        // Reset styles when not collapsed
        orgSwitcherButton.style.margin = "0";
      }
    }
  }, [isCollapsed, orgsList, org]);

  const isSettingsRoute = pathname.startsWith("/settings");
  const isHireCustomInDashboard = isAgentsHireCustomRoute && isSignedIn;

  const links: NavProps["links"] = [
    {
      title: "My organizations",
      icon: Building,
      variant: "ghost",
      href: "/settings/organizations",
      isHidden: !!orgRole,
    },
  ];

  const additionalLinks: NavProps["links"] = [
    {
      title: "Settings",
      icon: Settings,
      variant: "ghost",
      href: "/settings",
      isHidden: isHiddenForNonAdmins,
    },
    {
      title: "My Profile",
      icon: User,
      variant: "ghost",
      onClick: () => {
        openUserProfile();
      },
    },
    {
      title: "Organization",
      icon: Building2,
      variant: "ghost",
      isHidden: !isOrgAdmin,
      onClick: () => {
        openOrganizationProfile();
      },
    },
  ];

  const linksWithCurrent: NavProps["links"] = links
    .map((link) => {
      const output: NavProps["links"][0] =
        link.href === pathname ? { ...link } : link;

      return output;
    })
    .filter((link) => !link.isHidden);

  const additionalLinksWithCurrent: NavProps["links"] = additionalLinks
    .map((link) => {
      const output: NavProps["links"][0] =
        link.href === pathname ? { ...link } : link;

      return output;
    })
    .filter((link) => !link.isHidden);

  const agentCount = projects?.length;
  const agentCountLabel =
    agentCount === undefined
      ? null
      : `${agentCount} ${
          agentCount === 1
            ? brand.terminology.agent.singularLower
            : brand.terminology.agent.pluralLower
        }`;

  const dashboardMain = (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <nav
        data-testid="app-navbar"
        className="sticky top-0 z-40 shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      >
        <div className="flex h-14 items-center gap-2 px-3 md:h-16 md:gap-3 md:px-4">
          <div className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 shrink-0 p-0 md:hidden"
              aria-label="Open navigation"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            {orgsList.isLoaded && org.isLoaded ? (
              <ClerkOrgSwitcher
                compact={!isDesktop || (isSiteGlue && !isOrgAdmin)}
              />
            ) : null}

            {agentCountLabel ? (
              <span className="hidden whitespace-nowrap rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground sm:inline-flex">
                {agentCountLabel}
              </span>
            ) : null}
          </div>

          <div
            ref={setHeaderSlotRef}
            className="flex min-w-0 flex-1 items-center justify-end overflow-hidden"
          />

          <div className="flex shrink-0 items-center gap-1">
            <UserProfile marketingStyle={isSiteGlue} />
            <Button
              onClick={() =>
                void signOut({
                  redirectUrl: getPostSignOutRedirectUrl({
                    brandId: brand.id,
                    organizationName: org.organization?.name,
                    organizationSlug: org.organization?.slug,
                  }),
                })
              }
              variant={isSiteGlue ? "ghost" : "outline"}
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              aria-label="Log out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto",
          isHireCustomInDashboard ? "overflow-hidden p-0" : "px-4 md:px-8",
        )}
      >
        {children}
      </div>
    </div>
  );

  return shallSkipShellLayout ? (
    <>
      {isUserLoggedIn ? (
        <Suspense fallback={null}>
          <EnsureMarketingPortalAccess />
        </Suspense>
      ) : null}
      <main className="min-w-0 w-full">{children}</main>
    </>
  ) : (
    <TooltipProvider delayDuration={0}>
      <AppShellHeaderSlotProvider slotEl={headerSlotEl}>
      {isUserLoggedIn ? (
        <Suspense fallback={null}>
          <EnsureMarketingPortalAccess />
        </Suspense>
      ) : null}
      <div className="flex h-screen">
        {isSettingsRoute ? (
          <div className="flex min-w-0 flex-1 flex-col">
            <nav
              data-testid="app-navbar"
              className="sticky top-0 z-40 shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
            >
              <div className="flex h-16 items-center gap-3 px-4">
                <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3">
                  <Link href="/" className="flex min-w-0 shrink-0 items-center gap-2">
                    <Image
                      src={chrome.logoSrc}
                      alt={chrome.logoAlt}
                      width={32}
                      height={32}
                      className="h-8 w-8 rounded-md"
                      priority
                    />
                    <div className="hidden min-w-0 flex-col sm:flex">
                      {isSiteGlue ? (
                        <span className="text-lg font-bold leading-none tracking-tight">
                          <span style={{ color: chrome.primaryColor }}>Site</span>
                          <span style={{ color: chrome.accentColor }}>Glue</span>
                        </span>
                      ) : (
                        <span className="text-lg font-bold leading-none tracking-tight">
                          {chrome.productName}
                        </span>
                      )}
                    </div>
                  </Link>
                  {orgsList.isLoaded && org.isLoaded ? (
                    <ClerkOrgSwitcher
                      compact={!isDesktop || (isSiteGlue && !isOrgAdmin)}
                    />
                  ) : null}
                </div>
                <div className="flex min-w-0 flex-1" />
                <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                  <UserProfile marketingStyle={isSiteGlue} />
                  <Button
                    onClick={() =>
                      void signOut({
                        redirectUrl: getPostSignOutRedirectUrl({
                          brandId: brand.id,
                          organizationName: org.organization?.name,
                          organizationSlug: org.organization?.slug,
                        }),
                      })
                    }
                    variant={isSiteGlue ? "ghost" : "outline"}
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0"
                    aria-label="Log out"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </nav>
            <div className="h-full w-full overflow-y-auto px-4 md:px-8">{children}</div>
          </div>
        ) : isDesktop ? (
          <ResizablePanelGroup
            direction="horizontal"
            onLayout={(sizes: number[]) => {
              document.cookie = `react-resizable-panels:layout=${JSON.stringify(
                sizes,
              )};path=/`;
            }}
            className="h-full w-full"
          >
            <ResizablePanel
              defaultSize={defaultLayout[0]}
              collapsedSize={collapsedSize}
              collapsible={true}
              minSize={10}
              maxSize={15}
              onCollapse={() => {
                setIsCollapsed(true);
                document.cookie = `react-resizable-panels:collapsed=${JSON.stringify(
                  true,
                )};path=/`;
              }}
              onExpand={() => {
                setIsCollapsed(false);
                document.cookie = `react-resizable-panels:collapsed=${JSON.stringify(
                  false,
                )}`;
              }}
              className={cn(
                "flex flex-col border-r bg-background",
                isCollapsed &&
                  "min-w-[50px] transition-all duration-300 ease-in-out",
              )}
            >
              <AppShellSidebar
                isCollapsed={isCollapsed}
                chrome={chrome}
                isSiteGlue={isSiteGlue}
                brand={brand}
                projects={projects}
                isSidebarAgentsLoading={isSidebarAgentsLoading}
                orgRole={orgRole}
                additionalLinks={additionalLinksWithCurrent}
                onSupport={toggleChat}
              />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={defaultLayout[2]} className="min-w-0">
              {dashboardMain}
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          dashboardMain
        )}
        {!isDesktop && !isSettingsRoute ? (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent side="left" className="w-[280px] max-w-[85vw] p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <AppShellSidebar
                isCollapsed={false}
                chrome={chrome}
                isSiteGlue={isSiteGlue}
                brand={brand}
                projects={projects}
                isSidebarAgentsLoading={isSidebarAgentsLoading}
                orgRole={orgRole}
                additionalLinks={additionalLinksWithCurrent}
                onNavigate={() => setMobileNavOpen(false)}
                onSupport={toggleChat}
              />
            </SheetContent>
          </Sheet>
        ) : null}
      </div>
      </AppShellHeaderSlotProvider>
    </TooltipProvider>
  );
}
